const ZIP_VERSION = 20;
const XML_ENCODER = new TextEncoder();
const INVALID_XML_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uD800-\uDFFF\uFFFE\uFFFF]/g;

type ZipEntry = {
  name: string;
  data: Uint8Array;
};

const CRC32_LOOKUP = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < table.length; i++) {
    let crc = i;
    for (let bit = 0; bit < 8; bit++) {
      const shouldXor = (crc & 1) !== 0;
      crc = crc >>> 1;
      if (shouldXor) {
        crc ^= 0xedb88320;
      }
    }
    table[i] = crc >>> 0;
  }
  return table;
})();

export function createWorkbook(rows: string[][]): Blob {
  const files = [
    {
      name: "[Content_Types].xml",
      data: XML_ENCODER.encode(getContentTypesXml()),
    },
    {
      name: "_rels/.rels",
      data: XML_ENCODER.encode(getRootRelationshipsXml()),
    },
    {
      name: "xl/workbook.xml",
      data: XML_ENCODER.encode(getWorkbookXml()),
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: XML_ENCODER.encode(getWorkbookRelationshipsXml()),
    },
    {
      name: "xl/worksheets/sheet1.xml",
      data: XML_ENCODER.encode(getSheetXml(rows)),
    },
  ];

  const zipped = createZip(files);
  return new Blob([zipped], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function getContentTypesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
    "</Types>",
  ].join("");
}

function getRootRelationshipsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
    "</Relationships>",
  ].join("");
}

function getWorkbookXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    "<sheets>",
    '<sheet name="Sheet1" sheetId="1" r:id="rId1"/>',
    "</sheets>",
    "</workbook>",
  ].join("");
}

function getWorkbookRelationshipsXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>',
    "</Relationships>",
  ].join("");
}

function getSheetXml(rows: string[][]) {
  const maxColumns = Math.max(1, ...rows.map(row => row.length));
  const rowCount = Math.max(1, rows.length);
  const endCellRef = `${columnNumberToName(maxColumns)}${rowCount}`;

  const rowXml = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((rawValue, columnIndex) => {
          const cellRef = `${columnNumberToName(columnIndex + 1)}${rowIndex + 1}`;
          const cellValue = escapeXml(rawValue);
          return `<c r="${cellRef}" t="inlineStr"><is><t xml:space="preserve">${cellValue}</t></is></c>`;
        })
        .join("");

      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    `<dimension ref="A1:${endCellRef}"/>`,
    '<sheetViews><sheetView workbookViewId="0"/></sheetViews>',
    '<sheetFormatPr defaultRowHeight="15"/>',
    `<sheetData>${rowXml}</sheetData>`,
    "</worksheet>",
  ].join("");
}

function escapeXml(value: string) {
  return value
    .replace(INVALID_XML_CHARACTERS, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnNumberToName(value: number) {
  let column = "";
  let current = value;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    current = Math.floor((current - 1) / 26);
  }

  return column;
}

function createZip(entries: ZipEntry[]) {
  const localFileParts: Uint8Array[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  let fileOffset = 0;

  for (const entry of entries) {
    const fileNameBytes = XML_ENCODER.encode(entry.name);
    const data = entry.data;
    const crc = crc32(data);

    const localHeader = new Uint8Array(30 + fileNameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, ZIP_VERSION, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, fileNameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(fileNameBytes, 30);

    const centralHeader = new Uint8Array(46 + fileNameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, ZIP_VERSION, true);
    centralView.setUint16(6, ZIP_VERSION, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, fileNameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, fileOffset, true);
    centralHeader.set(fileNameBytes, 46);

    localFileParts.push(localHeader, data);
    centralDirectoryParts.push(centralHeader);

    fileOffset += localHeader.length + data.length;
  }

  const centralDirectoryOffset = fileOffset;
  const centralDirectory = concatUint8Arrays(centralDirectoryParts);
  const endOfCentralDirectory = new Uint8Array(22);
  const eocdView = new DataView(endOfCentralDirectory.buffer);
  eocdView.setUint32(0, 0x06054b50, true);
  eocdView.setUint16(4, 0, true);
  eocdView.setUint16(6, 0, true);
  eocdView.setUint16(8, entries.length, true);
  eocdView.setUint16(10, entries.length, true);
  eocdView.setUint32(12, centralDirectory.length, true);
  eocdView.setUint32(16, centralDirectoryOffset, true);
  eocdView.setUint16(20, 0, true);

  return concatUint8Arrays([...localFileParts, centralDirectory, endOfCentralDirectory]);
}

function concatUint8Arrays(arrays: Uint8Array[]) {
  const totalLength = arrays.reduce((sum, array) => sum + array.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  for (const array of arrays) {
    merged.set(array, offset);
    offset += array.length;
  }

  return merged;
}

function crc32(data: Uint8Array) {
  let crc = 0xffffffff;
  for (const value of data) {
    const index = (crc ^ value) & 0xff;
    crc = (crc >>> 8) ^ CRC32_LOOKUP[index];
  }
  return (crc ^ 0xffffffff) >>> 0;
}
